import React, { useEffect, useRef } from "react";
import ActualizaDatosAfiliacion from "./ActualizaDatosAfiliacion";

const CONSULTA_VARIANTS = {
  basic: {
    includePerfilLaft: false,
    hidePatrimonio: true,
    hideIngresosLaborales: true,
  },
  full: {
    includePerfilLaft: true,
    hidePatrimonio: false,
    hideIngresosLaborales: false,
  },
};

const normalizeUiText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const toButtonText = (node) =>
  String(node?.textContent || "")
    .trim()
    .toUpperCase();

const HIDDEN_BUTTON_KEYWORDS = [
  "AGREGAR",
  "ANADIR",
  "NUEVO",
  "EDITAR",
  "ELIMINAR",
  "BORRAR",
  "GUARDAR",
  "GUARDANDO",
  "ACTUALIZAR",
  "ACTUALIZANDO",
  "REGRESAR",
  "ADJUNTAR",
  "ADJUNTANDO",
  "SUBIR",
  "CARGAR",
  "SELECCIONAR ARCHIVO",
  "TOMAR FOTO",
  "TOMAR FOTOGRAFIA",
  "CAPTURAR FOTO",
];

const isTabButton = (button) => {
  if (!button) return false;
  const role = String(button.getAttribute("role") || "").toLowerCase();
  return role === "tab" || Boolean(button.closest('[role="tablist"]'));
};

const shouldHideButton = (button) => {
  const text = normalizeUiText(toButtonText(button));
  if (!text) return false;
  return HIDDEN_BUTTON_KEYWORDS.some((keyword) => text.includes(keyword));
};

const hasHiddenActionIcon = (button) => {
  if (!button || typeof button.querySelector !== "function") return false;
  return Boolean(
    button.querySelector(
      'svg[data-testid="DeleteOutlineIcon"], svg[data-testid="DeleteIcon"], svg[data-testid="EditIcon"], svg[data-testid="ModeEditOutlineIcon"], svg[data-testid="AddIcon"], svg[data-testid="AddCircleIcon"], svg[data-testid="AddCircleOutlineIcon"], svg[data-testid="SaveIcon"], svg[data-testid="UploadFileIcon"], svg[data-testid="FileUploadIcon"], svg[data-testid="CloudUploadIcon"], svg[data-testid="CameraAltIcon"], svg[data-testid="PhotoCameraIcon"]',
    ),
  );
};

const canStayEnabled = (button) => {
  if (!button) return false;
  if (isTabButton(button)) return true;
  const text = toButtonText(button);
  return text === "CERRAR" || text === "CANCELAR";
};

const disableInputElement = (input) => {
  if (!input) return;
  const inputType = String(input.type || "").toLowerCase();
  if (inputType === "hidden") return;
  if (inputType === "file") {
    input.disabled = true;
    input.setAttribute("aria-disabled", "true");
    input.setAttribute("tabindex", "-1");
    input.style.pointerEvents = "none";
    input.style.display = "none";
    return;
  }
  if (inputType === "checkbox" || inputType === "radio") {
    input.disabled = true;
    input.setAttribute("aria-disabled", "true");
    input.style.pointerEvents = "none";
    return;
  }
  input.readOnly = true;
  input.setAttribute("readonly", "readonly");
  input.setAttribute("tabindex", "-1");
};

const hideFormFieldByLabel = (rootNode, labelText) => {
  const wanted = normalizeUiText(labelText);
  rootNode.querySelectorAll("label").forEach((label) => {
    const labelValue = normalizeUiText(label.textContent);
    if (labelValue !== wanted) return;
    const formControl = label.closest(".MuiFormControl-root");
    if (formControl) formControl.style.display = "none";
  });
};

const hideFormFieldByLabelIncludes = (rootNode, labelPart) => {
  const wanted = normalizeUiText(labelPart);
  rootNode.querySelectorAll("label").forEach((label) => {
    const labelValue = normalizeUiText(label.textContent);
    if (!labelValue.includes(wanted)) return;
    const formControl = label.closest(".MuiFormControl-root");
    if (formControl) formControl.style.display = "none";
  });
};

const hideNodeByExactText = (rootNode, text) => {
  const wanted = normalizeUiText(text);
  rootNode.querySelectorAll("p, span, div").forEach((node) => {
    const value = normalizeUiText(node.textContent);
    if (value === wanted) node.style.display = "none";
  });
};

const findSearchContainer = (rootNode) => {
  if (!rootNode) return null;
  const candidates = Array.from(rootNode.querySelectorAll("h1,h2,h3,h4,h5,h6,p,span,div"));
  const titleNode = candidates.find((node) => {
    const text = normalizeUiText(node.textContent);
    return text.includes("BUSCAR AFILIADO") && text.length <= 120;
  });
  if (!titleNode) return null;
  return (
    titleNode.closest(".MuiPaper-root") ||
    titleNode.closest('[class*="MuiPaper-root"]') ||
    titleNode.closest("section,article,form,div")
  );
};

const shouldLockNode = (node, searchContainer) =>
  !searchContainer || !searchContainer.contains(node);

const unlockSearchArea = (searchContainer) => {
  if (!searchContainer) return;

  searchContainer.querySelectorAll("input").forEach((input) => {
    const inputType = String(input.type || "").toLowerCase();
    if (inputType === "hidden") return;
    input.disabled = false;
    input.readOnly = false;
    input.removeAttribute("disabled");
    input.removeAttribute("readonly");
    input.removeAttribute("aria-disabled");
    input.removeAttribute("tabindex");
    input.style.pointerEvents = "";
    if (inputType !== "file") {
      input.style.display = "";
    }
  });

  searchContainer.querySelectorAll("textarea").forEach((textarea) => {
    textarea.disabled = false;
    textarea.readOnly = false;
    textarea.removeAttribute("disabled");
    textarea.removeAttribute("readonly");
    textarea.removeAttribute("aria-disabled");
    textarea.removeAttribute("tabindex");
    textarea.style.pointerEvents = "";
  });

  searchContainer.querySelectorAll("select").forEach((select) => {
    select.disabled = false;
    select.removeAttribute("disabled");
    select.removeAttribute("aria-disabled");
    select.removeAttribute("tabindex");
    select.style.pointerEvents = "";
  });

  searchContainer
    .querySelectorAll(".MuiInputBase-root, .MuiTextField-root, .MuiFormControl-root")
    .forEach((node) => {
      node.style.pointerEvents = "";
      node.removeAttribute("data-consulta-lock");
    });

  searchContainer
    .querySelectorAll("button, .MuiButton-root, .MuiIconButton-root, [role='button']")
    .forEach((button) => {
      button.disabled = false;
      button.removeAttribute("disabled");
      button.removeAttribute("aria-disabled");
      button.style.pointerEvents = "";
      if (button.style.display === "none") {
        button.style.display = "";
      }
    });
};

const hideConsultaActionColumns = (rootNode, searchContainer) => {
  if (!rootNode) return;
  const headersToHide = new Set(["", "VER ADJUNTO"]);

  rootNode.querySelectorAll("table").forEach((table) => {
    if (!shouldLockNode(table, searchContainer)) return;
    const headerCells = Array.from(table.querySelectorAll("thead tr th"));
    if (!headerCells.length) return;

    const indexesToHide = headerCells
      .map((th, idx) => (headersToHide.has(normalizeUiText(th.textContent)) ? idx : -1))
      .filter((idx) => idx >= 0);
    if (!indexesToHide.length) return;

    table.querySelectorAll("tr").forEach((row) => {
      const cells = Array.from(row.children || []);
      indexesToHide.forEach((idx) => {
        const cell = cells[idx];
        if (!cell) return;
        cell.style.display = "none";
      });
    });
  });
};

const hideTabAndPanelByText = (rootNode, tabText, searchContainer) => {
  const wanted = normalizeUiText(tabText);
  const tabs = Array.from(rootNode.querySelectorAll('[role="tab"]'));

  tabs.forEach((tab) => {
    if (!shouldLockNode(tab, searchContainer)) return;
    const label = normalizeUiText(tab.textContent);
    if (!label.includes(wanted)) return;

    const panelId = tab.getAttribute("aria-controls");
    tab.style.display = "none";
    tab.setAttribute("aria-hidden", "true");
    tab.setAttribute("tabindex", "-1");

    if (panelId) {
      const panel = rootNode.querySelector(`#${panelId}`);
      if (panel) panel.style.display = "none";
    }

    if (tab.getAttribute("aria-selected") === "true") {
      const nextTab = tabs.find(
        (candidate) =>
          candidate !== tab &&
          shouldLockNode(candidate, searchContainer) &&
          candidate.style.display !== "none",
      );
      if (nextTab && typeof nextTab.click === "function") {
        nextTab.click();
      }
    }
  });
};

const applyReadOnlyMode = (rootNode, variantConfig = CONSULTA_VARIANTS.basic) => {
  if (!rootNode) return;

  const searchContainer = findSearchContainer(rootNode);
  rootNode.setAttribute("data-consulta-readonly", "true");
  unlockSearchArea(searchContainer);

  rootNode.querySelectorAll("input").forEach((input) => {
    if (!shouldLockNode(input, searchContainer)) return;
    disableInputElement(input);
  });

  rootNode.querySelectorAll("textarea").forEach((textarea) => {
    if (!shouldLockNode(textarea, searchContainer)) return;
    textarea.readOnly = true;
    textarea.setAttribute("readonly", "readonly");
    textarea.setAttribute("tabindex", "-1");
    textarea.style.pointerEvents = "none";
  });

  rootNode.querySelectorAll("select").forEach((select) => {
    if (!shouldLockNode(select, searchContainer)) return;
    select.disabled = true;
    select.setAttribute("aria-disabled", "true");
  });

  rootNode.querySelectorAll('[role="combobox"]').forEach((combo) => {
    if (!shouldLockNode(combo, searchContainer)) return;
    combo.setAttribute("aria-disabled", "true");
    combo.setAttribute("tabindex", "-1");
    combo.style.pointerEvents = "none";
  });

  rootNode.querySelectorAll(".MuiSelect-select").forEach((selectUi) => {
    if (!shouldLockNode(selectUi, searchContainer)) return;
    selectUi.setAttribute("aria-disabled", "true");
    selectUi.setAttribute("tabindex", "-1");
    selectUi.style.pointerEvents = "none";
  });

  rootNode.querySelectorAll(".MuiInputBase-root").forEach((inputBase) => {
    if (!shouldLockNode(inputBase, searchContainer)) return;
    inputBase.style.pointerEvents = "none";
  });

  rootNode
    .querySelectorAll(
      ".MuiTextField-root, .MuiFormControl-root, .MuiAutocomplete-root, .MuiFormControlLabel-root",
    )
    .forEach((fieldLike) => {
      if (!shouldLockNode(fieldLike, searchContainer)) return;
      fieldLike.style.pointerEvents = "none";
      fieldLike.setAttribute("data-consulta-lock", "true");
    });

  rootNode.querySelectorAll(".MuiFormControl-root").forEach((field) => {
    if (!shouldLockNode(field, searchContainer)) return;
    field.setAttribute("data-consulta-lock", "true");
  });

  rootNode
    .querySelectorAll(".MuiAutocomplete-popupIndicator, .MuiAutocomplete-clearIndicator")
    .forEach((button) => {
      if (!shouldLockNode(button, searchContainer)) return;
      button.setAttribute("disabled", "disabled");
      button.style.pointerEvents = "none";
      button.style.display = "none";
    });

  rootNode.querySelectorAll(".MuiSwitch-root, .MuiSwitch-switchBase").forEach((switchNode) => {
    if (!shouldLockNode(switchNode, searchContainer)) return;
    switchNode.style.pointerEvents = "none";
  });

  rootNode
    .querySelectorAll("button, .MuiButton-root, .MuiIconButton-root, [role='button']")
    .forEach((button) => {
      if (!shouldLockNode(button, searchContainer)) return;
      if (shouldHideButton(button) || hasHiddenActionIcon(button)) {
        button.style.display = "none";
        return;
      }
      if (canStayEnabled(button)) return;
      button.setAttribute("disabled", "disabled");
      button.setAttribute("aria-disabled", "true");
      button.style.pointerEvents = "none";
    });

  hideFormFieldByLabel(rootNode, "Descripción del adjunto");
  hideFormFieldByLabel(rootNode, "Usuario que realizó el registro");
  hideFormFieldByLabel(rootNode, "Oficina donde registró");
  hideFormFieldByLabel(rootNode, "Usuario que actualizó");
  hideFormFieldByLabelIncludes(rootNode, "Usuario que realizó el registro");
  hideFormFieldByLabelIncludes(rootNode, "Oficina donde registró");
  hideFormFieldByLabelIncludes(rootNode, "Usuario que actualizó");
  if (variantConfig.hideIngresosLaborales) {
    hideFormFieldByLabel(rootNode, "(*) Ingresos aproximados");
    hideFormFieldByLabel(rootNode, "(*) Ingresos aproximados (monto)");
    hideFormFieldByLabelIncludes(rootNode, "Ingresos aproximados");
    hideFormFieldByLabelIncludes(rootNode, "Ingresos aproximados (monto)");
  }
  if (variantConfig.hidePatrimonio) {
    hideTabAndPanelByText(rootNode, "PATRIMONIO", searchContainer);
  }
  hideNodeByExactText(rootNode, "Puede adjuntar uno o varios archivos.");
  hideConsultaActionColumns(rootNode, searchContainer);
};

export default function ConsultarDatosAfiliacion({ variant = "basic" }) {
  const rootRef = useRef(null);
  const rafRef = useRef(0);
  const variantConfig = CONSULTA_VARIANTS[variant] || CONSULTA_VARIANTS.basic;

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    const syncReadOnly = () => {
      applyReadOnlyMode(node, variantConfig);
    };

    syncReadOnly();

    const observer = new MutationObserver(() => {
      if (rafRef.current) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = 0;
        syncReadOnly();
      });
    });
    observer.observe(node, {
      subtree: true,
      childList: true,
    });

    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      observer.disconnect();
    };
  }, [variantConfig]);

  return (
    <div ref={rootRef}>
      <ActualizaDatosAfiliacion
        includePerfilLaft={variantConfig.includePerfilLaft}
      />
    </div>
  );
}
